import { useMemo } from 'react'
import { useAnalyticsOverview } from './provider'
import type { AnalyticsOverview } from '@mission.io/mission-toolkit'

/**
 * Get the total number of points gathered during the mission
 *
 * @return {?number} - the number of points gathered during the mission, or null if it could not be calculated
 */
export function useFinalScore(): ?number {
	const { overView } = useAnalyticsOverview()
	return overView?.finalScore
}

/**
 * Get the student point average for the mission
 *
 * @return {?number} - the average of the students points, or null if it could not be calculated
 */
export function useAverageScorePerStudent(): ?number {
	const { overView } = useAnalyticsOverview()
	return overView?.averageStudentScore
}

/**
 * Determine if the analytics are available
 *
 * @return {?number} - true if the analytics are available, false otherwise
 */
export function useWasAnalyticsReceived(): boolean {
	const { analyticsReceived } = useAnalyticsOverview()
	return analyticsReceived
}

/**
 * Get the percentage of questions which were answered correctly
 *
 * @return {?number} - the percentage of the questions answered correct, or null if it could not be calculated
 */
export function useAnalyticsQuestionPercent(): ?number {
	const { overView } = useAnalyticsOverview()
	return useMemo(() => getQuestionPercentage(overView), [overView])
}

/**
 * getQuestionPercentage - Get the percentage of questions which were answered correctly
 *
 * @param  {?AnalyticsOverview} analyticsOverview - the analytics overview for the analytics of the mission
 *
 * @return {?number} - the percentage of the questions answered correct, or null if it could not be calculated
 */
export function getQuestionPercentage(
	analyticsOverview: ?AnalyticsOverview
): ?number {
	const { questions } = analyticsOverview?.missionStatistics || {
		questions: null,
	}
	if (!analyticsOverview || !questions || !questions.outOf) {
		return null
	}
	return Math.ceil((questions.score / questions.outOf) * 100)
}
